import { useState, useEffect, useCallback } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import PipelineStageBuilder from "./components/PipelineStageBuilder";
import CustomFieldsBuilder from "./components/CustomFieldsBuilder";
import LeadSourcesConfig from "./components/LeadSourcesConfig";
import IndustryTemplates from "./components/IndustryTemplates";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

interface Stage {
  id: string;
  name: string;
  color: string;
  required: boolean;
  sortOrder?: number;
  extraFields?: any[];
}

interface CustomField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  showInList: boolean;
  active: boolean;
  options?: string[];
  category: "lead" | "company";
}

interface LeadSource {
  id: string;
  name: string;
  color: string;
  active: boolean;
  priority: "High" | "Medium" | "Low";
}

export default function PipelineConfigPage() {
  const { showToast } = useToast();
  const [pipelineStages, setPipelineStages] = useState<Stage[]>([]);
  const [leadFields, setLeadFields] = useState<CustomField[]>([]);
  const [companyFields, setCompanyFields] = useState<CustomField[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, fieldsRes, sourcesRes] = await Promise.all([
        fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, []),
        fetchApi<any[]>('/config/custom-fields/get-all', undefined, []),
        fetchApi<any[]>('/config/lead-sources/get-all', undefined, []),
      ]);

      const stagesData = Array.isArray(stagesRes) ? stagesRes : (stagesRes as any)?.data || [];
      const fieldsData = Array.isArray(fieldsRes) ? fieldsRes : (fieldsRes as any)?.data || [];
      const sourcesData = Array.isArray(sourcesRes) ? sourcesRes : (sourcesRes as any)?.data || [];

      setPipelineStages(stagesData.map((s: any) => ({
        id: String(s.id),
        name: s.name,
        color: s.color || "#3b82f6",
        required: s.required ?? false,
        sortOrder: s.sort_order ?? s.sortOrder ?? 0,
        extraFields: (s.extraFields || []).map((f: any) => ({ ...f, id: String(f.id) })),
      })));

      setLeadFields(fieldsData.filter((f: any) => f.category === 'lead').map((f: any) => ({
        id: String(f.id),
        label: f.label,
        type: f.type,
        required: f.required ?? false,
        showInList: f.show_in_list ?? f.showInList ?? false,
        active: f.active ?? true,
        options: f.options || [],
        category: 'lead',
      })));

      setCompanyFields(fieldsData.filter((f: any) => f.category === 'company').map((f: any) => ({
        id: String(f.id),
        label: f.label,
        type: f.type,
        required: f.required ?? false,
        showInList: f.show_in_list ?? f.showInList ?? false,
        active: f.active ?? true,
        options: f.options || [],
        category: 'company',
      })));

      setLeadSources(sourcesData.map((src: any) => ({
        id: String(src.id),
        name: src.name,
        color: src.color || "#6b7280",
        active: src.active ?? true,
        priority: src.priority || "Medium",
      })));
    } catch (err) {
      console.error("Error fetching pipeline config:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSaveStages = async (stages: Stage[]) => {
    try {
      await fetchApi('/config/pipeline-stages/bulk-save', {
        method: 'POST',
        body: JSON.stringify({ stages }),
      });
      showToast("Pipeline stages saved successfully", "success");
      await fetchAll();
    } catch (err: any) {
      showToast(err.message || "Failed to save stages", "error");
    }
  };

  const handleSaveFields = async (lead: CustomField[], company: CustomField[]) => {
    try {
      await fetchApi('/config/custom-fields/bulk-save', {
        method: 'POST',
        body: JSON.stringify({ leadFields: lead, companyFields: company }),
      });
      showToast("Custom fields saved successfully", "success");
      await fetchAll();
    } catch (err: any) {
      showToast(err.message || "Failed to save custom fields", "error");
    }
  };

  const handleSaveSources = async (sources: LeadSource[]) => {
    try {
      await fetchApi('/config/lead-sources/bulk-save', {
        method: 'POST',
        body: JSON.stringify({ sources }),
      });
      showToast("Lead sources saved successfully", "success");
      await fetchAll();
    } catch (err: any) {
      showToast(err.message || "Failed to save lead sources", "error");
    }
  };

  return (
    <CrmLayout title="Pipeline & Field Configuration">
      <div className="p-5 md:p-6 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Pipeline &amp; Field Configuration</h1>
          <p className="text-sm text-gray-500 mt-0.5">Configure stages, custom fields, lead sources and industry templates</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <IndustryTemplates
              onSwitchApplied={fetchAll}
            />
            <PipelineStageBuilder
              stages={pipelineStages}
              onStagesChange={setPipelineStages}
              onSave={handleSaveStages}
            />
            <CustomFieldsBuilder
              leadFields={leadFields}
              companyFields={companyFields}
              onFieldsChange={(l, c) => { setLeadFields(l); setCompanyFields(c); }}
              onSave={handleSaveFields}
            />
            <LeadSourcesConfig
              sources={leadSources}
              onSourcesChange={setLeadSources}
              onSave={handleSaveSources}
            />
          </>
        )}
      </div>
    </CrmLayout>
  );
}